/** Pure helpers for the UCI protocol spoken by Stockfish. No browser APIs here. */

export type Score = { cp?: number; mate?: number };

export type InfoLine = {
  depth: number;
  multipv: number;
  score: Score; // from the side to move's point of view, as UCI reports it
  pv: string[];
  nodes?: number;
  nps?: number;
  bound?: "lower" | "upper";
};

/** Parses an `info ... pv ...` line. Returns null for lines without a score and PV. */
export function parseInfo(line: string): InfoLine | null {
  line = line.trim();
  if (!line.startsWith("info ")) return null;
  const t = line.split(/\s+/);
  let depth = 0;
  let multipv = 1;
  let nodes: number | undefined;
  let nps: number | undefined;
  let bound: InfoLine["bound"];
  const score: Score = {};
  let pv: string[] = [];
  let hasScore = false;
  for (let i = 1; i < t.length; i++) {
    switch (t[i]) {
      case "depth":
        depth = Number(t[++i]);
        break;
      case "multipv":
        multipv = Number(t[++i]);
        break;
      case "nodes":
        nodes = Number(t[++i]);
        break;
      case "nps":
        nps = Number(t[++i]);
        break;
      case "score": {
        const kind = t[++i];
        const v = Number(t[++i]);
        if (kind === "cp") score.cp = v;
        else if (kind === "mate") score.mate = v;
        hasScore = true;
        if (t[i + 1] === "lowerbound" || t[i + 1] === "upperbound") {
          bound = t[++i] === "lowerbound" ? "lower" : "upper";
        }
        break;
      }
      case "pv":
        pv = t.slice(i + 1);
        i = t.length;
        break;
    }
  }
  if (!hasScore || pv.length === 0) return null;
  return { depth, multipv, score, pv, nodes, nps, bound };
}

/** Parses `bestmove e2e4 ponder e7e5`. "(none)" means no legal move. */
export function parseBestMove(line: string): { best: string | null; ponder?: string } | null {
  const m = /^bestmove\s+(\S+)(?:\s+ponder\s+(\S+))?/.exec(line);
  if (!m) return null;
  return { best: m[1] === "(none)" ? null : m[1], ponder: m[2] };
}

/** Flips a side-to-move score to White's point of view. */
export function toWhitePov(score: Score, sideToMove: "w" | "b"): Score {
  if (sideToMove === "w") return { ...score };
  return {
    cp: score.cp === undefined ? undefined : -score.cp,
    mate: score.mate === undefined ? undefined : -score.mate,
  };
}

/** Converts a score to a single centipawn number, mapping mates to ±(10000 - distance). */
export function scoreToCp(score: Score): number {
  if (score.mate !== undefined) {
    if (score.mate === 0) return 0;
    return score.mate > 0 ? 10000 - score.mate : -10000 - score.mate;
  }
  return score.cp ?? 0;
}

/** Human-readable evaluation such as "+1.3", "-0.4" or "#3" / "#-2". */
export function formatScore(score: Score): string {
  if (score.mate !== undefined) return `#${score.mate}`;
  // Round the magnitude first so small negatives show as "0.0", not "-0.0".
  const cp = score.cp ?? 0;
  const pawns = Math.round(Math.abs(cp) / 10) / 10;
  return `${pawns === 0 ? "" : cp > 0 ? "+" : "-"}${pawns.toFixed(1)}`;
}
